import { Button } from '@monksflow/monks-ui';
import { DownloadIcon, PlusIcon } from 'lucide-react';
import {
  CONNECTOR_PLATFORM_ICONS,
  CONNECTOR_PLATFORM_LABELS,
  type ConnectorPlatform,
} from '@/entities/media-connector';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import {
  JOB_STATUS_FILTER_LABELS,
  type JobFilterState,
  type JobStatusFilter,
} from '../model/filters';
import { PendingSkeleton } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useTranslation } from 'react-i18next';

const STATUS_OPTIONS = (Object.keys(JOB_STATUS_FILTER_LABELS) as JobStatusFilter[]).map(
  (value) => ({
    value,
    label: JOB_STATUS_FILTER_LABELS[value],
  }),
);

export interface DeliveryJobToolbarProps {
  filters: JobFilterState;
  /** Only the platforms this project's jobs actually touch. */
  platforms: ConnectorPlatform[];
  onFiltersChange: (next: JobFilterState) => void;
  onNewJob: () => void;
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
  /** Swaps the controls for placeholders while the section is still resolving. */
  pending?: boolean;
}

/**
 * Toolbar for the Jobs view, laid out like the Projects toolbar: search plus
 * filter chips on the left, the primary create action on the right.
 *
 * The platform options are derived from the jobs on screen rather than the full
 * connector list — offering a filter that can only ever return nothing is worse
 * than not offering it.
 */
export function DeliveryJobToolbar(props: DeliveryJobToolbarProps) {
  const {
    filters,
    platforms,
    onFiltersChange,
    onNewJob,
    onDownloadTemplate,
    isDownloadingTemplate,
  } = props;
  const { t } = useTranslation('projects');

  const platformOptions = platforms.map((platform) => ({
    value: platform,
    label: CONNECTOR_PLATFORM_LABELS[platform],
    icon: (
      <img
        src={CONNECTOR_PLATFORM_ICONS[platform]}
        alt=""
        aria-hidden
        className="size-4 rounded-sm object-contain"
      />
    ),
  }));

  return (
    <div data-testid="delivery-job-toolbar" className="flex items-center justify-between p-section">
      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <div className="flex items-center gap-2">
          <SearchInput
            className="w-80"
            placeholder={t('deliveryJobGrid.deliveryJobToolbar.searchJobs')}
            aria-label={t('deliveryJobGrid.deliveryJobToolbar.searchJobsLabel')}
            value={filters.search}
            onChange={(search) => onFiltersChange({ ...filters, search })}
          />

          <FilterChipPopover
            label={t('deliveryJobGrid.deliveryJobToolbar.status')}
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={(status) => onFiltersChange({ ...filters, status })}
            resetOptionLabel={t('deliveryJobGrid.deliveryJobToolbar.allStatuses')}
            searchable={false}
          />

          {platformOptions.length > 1 && (
            <FilterChipPopover
              label={t('deliveryJobGrid.deliveryJobToolbar.platform')}
              options={platformOptions}
              value={filters.platform}
              onChange={(platform) => onFiltersChange({ ...filters, platform })}
              resetOptionLabel={t('deliveryJobGrid.deliveryJobToolbar.allPlatforms')}
              searchable={false}
            />
          )}
        </div>
      </PendingSkeleton>

      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <div className="flex items-center gap-2">
          {/* Next to New job because that is where someone stands when they
            realise they have nothing to upload yet. */}
          <Button
            variant="outline"
            size="default"
            onClick={onDownloadTemplate}
            disabled={isDownloadingTemplate}
            data-testid="delivery-download-template-button"
          >
            <DownloadIcon className="size-4" />
            {isDownloadingTemplate
              ? t('deliveryJobGrid.deliveryJobToolbar.preparing')
              : t('deliveryJobGrid.deliveryJobToolbar.template')}
          </Button>

          <Button size="default" onClick={onNewJob} data-testid="delivery-new-job-button">
            <PlusIcon className="size-4" />
            {t('deliveryJobGrid.deliveryJobToolbar.newJob')}
          </Button>
        </div>
      </PendingSkeleton>
    </div>
  );
}
